import { inject, Injectable } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { MediaHubApiService } from './media-hub-api.service'
import { ChatSession } from '../models/chat.model'

export interface CreateChatRequest {
  mediaAssetId?: string
  projectId?: string
}

export interface CreateChatResponse {
  chat: ChatSession
  message: string
  success: boolean
}

export interface SendMessageRequest {
  message: string
  selectedSources?: string[]
}

export interface SendMessageResponse {
  chat: ChatSession
  success:boolean
}

export interface GetChatsResponse {
  chats: ChatSession[]
  count: number
}

export interface GetChatResponse {
  success: boolean
  chat: ChatSession
}

export interface AddChatToProjectRequest {
  projectId: string
}

export interface AddChatToProjectResponse {
  chat: ChatSession
  message: string
  success:boolean
}

export interface ArchiveChatResponse {
  chat: ChatSession
  message: string
}

export interface DeleteChatResponse {
  success: boolean
  message: string
}

export interface UpdateChatTitleRequest {
  title: string
}

export interface UpdateChatTitleResponse {
  chat: ChatSession
  message: string
  success: boolean
}

export interface ChatQueryParams {
  mediaAssetId?: string
  projectId?: string
}

export interface AddPlaylistsToChatRequest {
  playlistIds: string[]
}

export interface AddPlaylistsToChatResponse {
  chat: ChatSession
  message: string
  success: boolean
}

export interface RemovePlaylistsFromChatRequest {
  playlistIds: string[]
}

export interface RemovePlaylistsFromChatResponse {
  chat: ChatSession
  message: string
  success: boolean
}

export interface ShareChatSnapshotRequest {
  targetUserIds: string[]
}

export interface ShareChatSnapshotResponse {
  message: string
  success: boolean
  sharedChats?: Array<{
    chatId: string
    userId: string
  }>
}

@Injectable({
  providedIn: 'root',
})
export class AssetChatApiService {
  private readonly endpoint = '/media-asset-chats'
  private apiService = inject(MediaHubApiService)

  createAssetChat(request: CreateChatRequest): Promise<CreateChatResponse> {
    return this.apiService.postBase<CreateChatRequest, CreateChatResponse>(this.endpoint, request)
  }

  createAssetChat$(request: CreateChatRequest): Observable<CreateChatResponse> {
    return this.apiService.postBaseRx<CreateChatRequest, CreateChatResponse>(this.endpoint, request)
  }

  sendMessage(chatId: string, request: SendMessageRequest): Promise<SendMessageResponse> {
    return this.apiService.postBase<SendMessageRequest, SendMessageResponse>(`${this.endpoint}/${chatId}/message`, request)
  }

  sendMessage$(chatId: string, request: SendMessageRequest): Observable<SendMessageResponse> {
    return this.apiService.postBaseRx<SendMessageRequest, SendMessageResponse>(`${this.endpoint}/${chatId}/message`, request)
  }

  getAssetChats(filters?: ChatQueryParams): Promise<GetChatResponse> {
    const params = this.buildQueryParams(filters)
    return this.apiService.getBase<GetChatResponse>(this.endpoint, params)
  }

  getAssetChats$(filters?: ChatQueryParams): Observable<GetChatsResponse> {
    const params = this.buildQueryParams(filters)
    return this.apiService.getBaseRx<GetChatsResponse>(this.endpoint, params)
  }

  getAssetChatsByAsset(mediaAssetId: string): Promise<GetChatResponse> {
    return this.getAssetChats({ mediaAssetId })
  }

  getAssetChatsByAsset$(mediaAssetId: string): Observable<GetChatsResponse> {
    return this.getAssetChats$({ mediaAssetId })
  }

  getAssetChatsByProject(projectId: string): Promise<GetChatResponse> {
    return this.getAssetChats({ projectId })
  }

  getAssetChatsByProject$(projectId: string): Observable<GetChatsResponse> {
    return this.getAssetChats$({ projectId })
  }

  getSingleChat(chatId: string): Promise<GetChatResponse> {
    return this.apiService.getBase<GetChatResponse>(`${this.endpoint}/${chatId}`)
  }

  getSingleChat$(chatId: string): Observable<GetChatResponse> {
    return this.apiService.getBaseRx<GetChatResponse>(`${this.endpoint}/${chatId}`)
  }

  addPlaylistsToChat(chatId: string, request: AddPlaylistsToChatRequest): Promise<AddPlaylistsToChatResponse> {
    return this.apiService.postBase<AddPlaylistsToChatRequest, AddPlaylistsToChatResponse>(`${this.endpoint}/${chatId}/playlists`, request)
  }

  addPlaylistsToChat$(chatId: string, request: AddPlaylistsToChatRequest): Observable<AddPlaylistsToChatResponse> {
    return this.apiService.postBaseRx<AddPlaylistsToChatRequest, AddPlaylistsToChatResponse>(`${this.endpoint}/${chatId}/playlists`, request)
  }

  removePlaylistsFromChat(chatId: string, request: RemovePlaylistsFromChatRequest): Promise<RemovePlaylistsFromChatResponse> {
    return this.apiService.deleteBaseWithBody<RemovePlaylistsFromChatRequest, RemovePlaylistsFromChatResponse>(`${this.endpoint}/${chatId}/playlists`, request)
  }

  removePlaylistsFromChat$(chatId: string, request: RemovePlaylistsFromChatRequest): Observable<RemovePlaylistsFromChatResponse> {
    return this.apiService.deleteBaseWithBodyRx<RemovePlaylistsFromChatRequest, RemovePlaylistsFromChatResponse>(`${this.endpoint}/${chatId}/playlists`, request)
  }

  archiveChat(chatId: string): Promise<ArchiveChatResponse> {
    return this.apiService.postBase<void, ArchiveChatResponse>(`${this.endpoint}/${chatId}/archive`)
  }

  archiveChat$(chatId: string): Observable<ArchiveChatResponse> {
    return this.apiService.postBaseRx<void, ArchiveChatResponse>(`${this.endpoint}/${chatId}/archive`)
  }

  deleteChat(chatId: string): Promise<DeleteChatResponse> {
    return this.apiService.deleteBase<DeleteChatResponse>(`${this.endpoint}/${chatId}`)
  }

  deleteChat$(chatId: string): Observable<DeleteChatResponse> {
    return this.apiService.deleteBaseRx<DeleteChatResponse>(`${this.endpoint}/${chatId}`)
  }

  updateChatTitle(chatId: string, request: UpdateChatTitleRequest): Promise<UpdateChatTitleResponse> {
    return this.apiService.putBase<UpdateChatTitleRequest, UpdateChatTitleResponse>(`${this.endpoint}/${chatId}`, request)
  }

  updateChatTitle$(chatId: string, request: UpdateChatTitleRequest): Observable<UpdateChatTitleResponse> {
    return this.apiService.putBaseRx<UpdateChatTitleRequest, UpdateChatTitleResponse>(`${this.endpoint}/${chatId}`, request)
  }

  shareChatSnapshot(chatId: string, request: ShareChatSnapshotRequest): Promise<ShareChatSnapshotResponse> {
    return this.apiService.postBase<ShareChatSnapshotRequest, ShareChatSnapshotResponse>(`${this.endpoint}/${chatId}/share-snapshot`, request)
  }

  shareChatSnapshot$(chatId: string, request: ShareChatSnapshotRequest): Observable<ShareChatSnapshotResponse> {
    return this.apiService.postBaseRx<ShareChatSnapshotRequest, ShareChatSnapshotResponse>(`${this.endpoint}/${chatId}/share-snapshot`, request)
  }

  sendMessageStreaming(chatId: string, request: SendMessageRequest, token?: string): AsyncGenerator<string> {
    return this.apiService.postStreaming<SendMessageRequest>(`${this.endpoint}/${chatId}/message`, request, token)
  }

  private buildQueryParams(filters?: ChatQueryParams): HttpParams | undefined {
    if (!filters) return undefined

    let params = new HttpParams()

    if (filters.mediaAssetId) {
      params = params.set('mediaAssetId', filters.mediaAssetId)
    }

    if (filters.projectId) {
      params = params.set('projectId', filters.projectId)
    }

    return params
  }
}
